import { useState, useRef, useEffect } from "react";
import { useAllModulesConfig } from "@/hooks/useAuthConfig";
import { isAdminPreview } from "@/hooks/useModuleVisibility";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Stethoscope, Building2, Pill, FlaskConical, Droplets, Truck, Heart,
  CalendarHeart, Sparkles, Shield, Plane, Package, ChevronRight,
  Brain, Zap, Video, MapPin, Upload, PhoneCall, ArrowRight, Dumbbell,
  TrendingUp, ChevronDown
} from "lucide-react";
import { cn } from "@/lib/utils";

type Sub = { label: string; href: string; hot?: boolean };
type Cat = {
  key: string;
  label: string;
  desc: string;
  icon: typeof Stethoscope;
  href: string;
  subs: Sub[];
  badge?: "LIVE" | "SOON" | "NEW";
    moduleKey?: string | null;
};

const CATEGORIES: Cat[] = [
  { key: "doctors", label: "Doctors", moduleKey: 'doctor', desc: "Verified specialists", icon: Stethoscope, href: "/listings?category=2",
    subs: [
      { label: "Cardiologist", href: "/listings?category=2&search=Cardiologist", hot: true },
      { label: "Dentist", href: "/listings?category=2&search=Dentist" },
      { label: "Neurologist", href: "/listings?category=2&search=Neurologist" },
      { label: "Pediatrician", href: "/listings?category=2&search=Pediatrician" },
      { label: "Orthopedic", href: "/listings?category=2&search=Orthopedic" },
      { label: "Gynecologist", href: "/listings?category=2&search=Gynecologist" },
    ]},
  { key: "hospitals", label: "Hospitals", moduleKey: 'hospital', desc: "Multi & super-speciality", icon: Building2, href: "/listings?category=1",
    subs: [
      { label: "Multi Speciality", href: "/listings?category=1&search=Multi" },
      { label: "Eye Hospital", href: "/listings?category=1&search=Eye" },
      { label: "IVF Center", href: "/listings?category=1&search=IVF", hot: true },
      { label: "Cancer Hospital", href: "/listings?category=1&search=Cancer" },
      { label: "Dental Clinic", href: "/listings?category=1&search=Dental" },
      { label: "Children Hospital", href: "/listings?category=1&search=Children" },
    ]},
  { key: "pharmacy", label: "Pharmacy", moduleKey: 'medical_store', desc: "24x7 medicine & supplies", icon: Pill, href: "/listings?category=3",
    subs: [
      { label: "24x7 Pharmacy", href: "/listings?category=3&search=24x7", hot: true },
      { label: "Ayurvedic", href: "/listings?category=3&search=Ayurvedic" },
      { label: "Homeopathic", href: "/listings?category=3&search=Homeopathic" },
      { label: "Surgical Supply", href: "/listings?category=3&search=Surgical" },
    ]},
  { key: "diagnostics", label: "Diagnostics", moduleKey: 'diagnostic', desc: "Tests, scans & pathology", icon: FlaskConical, href: "/listings?category=6",
    subs: [
      { label: "MRI Scan", href: "/listings?category=6&search=MRI", hot: true },
      { label: "CT Scan", href: "/listings?category=6&search=CT" },
      { label: "X-Ray", href: "/listings?category=6&search=X-Ray" },
      { label: "Blood Test", href: "/listings?category=6&search=Blood" },
      { label: "Pathology", href: "/listings?category=6&search=Pathology" },
      { label: "Ultrasound", href: "/listings?category=6&search=Ultrasound" },
    ]},
  { key: "blood", label: "Blood Banks", moduleKey: 'blood_bank', desc: "Real-time availability", icon: Droplets, href: "/blood-banks",
    subs: [
      { label: "A+ / A−", href: "/blood-banks?group=A" },
      { label: "B+ / B−", href: "/blood-banks?group=B" },
      { label: "O+ / O−", href: "/blood-banks?group=O", hot: true },
      { label: "AB+ / AB−", href: "/blood-banks?group=AB" },
      { label: "Platelets", href: "/blood-banks?type=platelets" },
      { label: "Plasma", href: "/blood-banks?type=plasma" },
    ]},
  { key: "ambulance", label: "Ambulance", moduleKey: 'ambulance', desc: "24/7 emergency dispatch", icon: Truck, href: "/ambulance",
    subs: [
      { label: "Basic Life Support", href: "/ambulance?type=bls" },
      { label: "Advanced Life Support", href: "/ambulance?type=als" },
      { label: "ICU Ambulance", href: "/ambulance?type=icu" },
      { label: "Air Ambulance", href: "/ambulance?type=air", hot: true },
    ]},
  { key: "homecare", label: "HomeCare+", moduleKey: 'homecare', desc: "Nurse, physio & elderly", icon: Heart, href: "/homecare",
    subs: [
      { label: "Nursing at Home", href: "/homecare?service=nursing" },
      { label: "Physiotherapy", href: "/homecare?service=physio" },
      { label: "Elderly Care", href: "/homecare?service=elderly" },
      { label: "Post-Surgical", href: "/homecare?service=post-surgical" },
      { label: "Mother & Baby", href: "/homecare?service=mother-baby" },
    ]},
  { key: "camps", label: "Health Camps", moduleKey: 'health_camp', desc: "Free & discounted checkups", icon: CalendarHeart, href: "/health-camps",
    subs: [
      { label: "Eye Camps", href: "/health-camps?type=eye" },
      { label: "Dental Camps", href: "/health-camps?type=dental" },
      { label: "Cardiac Screening", href: "/health-camps?type=cardiac" },
      { label: "Diabetes Camps", href: "/health-camps?type=diabetes" },
    ]},
  { key: "wholesalers", label: "Wholesalers", moduleKey: 'wholesaler', desc: "Bulk medicines & equipment", icon: Package, href: "/listings?category=7",
    subs: [
      { label: "Bulk Medicines", href: "/listings?category=7" },
      { label: "Surgical Equipment", href: "/listings?category=7&search=Surgical" },
      { label: "Disposables", href: "/listings?category=7&search=Disposables" },
      { label: "Lab Supplies", href: "/listings?category=7&search=Lab" },
    ]},
  { key: "wellness", label: "Wellness", desc: "Fitness, yoga & gym", icon: Dumbbell, href: "/listings?category=11", badge: "SOON",
    subs: [
      { label: "Gym & Fitness", href: "/listings?category=11" },
      { label: "Yoga Centers", href: "/listings?category=11&search=Yoga" },
      { label: "Nutritionist", href: "/listings?category=11&search=Nutritionist" },
    ]},
  { key: "insurance", label: "Insurance", desc: "Health cover & cashless", icon: Shield, href: "/listings", badge: "SOON",
    subs: [
      { label: "Health Insurance", href: "/listings" },
      { label: "Cashless Hospitals", href: "/listings?cashless=1" },
      { label: "Family Floater", href: "/listings" },
    ]},
  { key: "tourism", label: "Medical Tourism", desc: "International patient care", icon: Plane, href: "/listings", badge: "SOON",
    subs: [
      { label: "Cardiac Surgery", href: "/listings?search=Cardiac" },
      { label: "Cancer Treatment", href: "/listings?search=Cancer" },
      { label: "Cosmetic Surgery", href: "/listings?search=Cosmetic" },
    ]},
];

const AI_TOOLS = [
  { icon: Brain, label: "AI Symptom Checker", desc: "Instant insights", href: "/" },
  { icon: Zap, label: "Smart Doctor Match", desc: "Find your specialist", href: "/listings?category=2" },
  { icon: Video, label: "Online Consultation", desc: "Talk to a doctor now", href: "/listings?category=2" },
];

const QUICK_ACTIONS = [
  { icon: Truck, label: "Emergency Ambulance", href: "/ambulance" },
  { icon: MapPin, label: "Doctor Near Me", href: "/listings?category=2" },
  { icon: FlaskConical, label: "Book a Lab Test", href: "/listings?category=6" },
  { icon: Upload, label: "Upload Prescription", href: "/patient/files" },
  { icon: PhoneCall, label: "24x7 Helpline", href: "tel:+911800000000" },
];

const TRENDING = [
  "Best Cardiologist", "24x7 Pharmacy", "MRI Scan", "Emergency Ambulance",
  "Dentist", "Blood A+ Required", "Eye Specialist", "IVF Center",
];

const BadgePill = ({ kind }: { kind: "LIVE" | "SOON" | "NEW" }) => {
  const cls =
    kind === "LIVE" ? "bg-red-500/10 text-red-600 ring-red-500/20" :
    kind === "NEW"  ? "bg-healing-green/10 text-healing-green ring-healing-green/20" :
                      "bg-cta-orange/10 text-cta-orange ring-cta-orange/20";
  return (
    <span className={cn("text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-full ring-1", cls)}>
      {kind}
    </span>
  );
};

const PremiumMegaMenu = () => {
  const [open, setOpen] = useState(false);
  const [activeKey, setActiveKey] = useState<string>(CATEGORIES[0].key);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const onEnter = () => { if (closeTimer.current) clearTimeout(closeTimer.current); setOpen(true); };
  const onLeave = () => { closeTimer.current = setTimeout(() => setOpen(false), 160); };

  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);
  const moduleConfig = useAllModulesConfig();
  const adminPreview = isAdminPreview();
  const isCatVisible = (key: string | null | undefined) => {
    if (!key) return true;
    if (adminPreview) return true;
    const status = moduleConfig?.[key]?.visibility_status ?? 'LIVE';
    return status === 'LIVE';
  };
  const visibleCategories = CATEGORIES.filter(c => isCatVisible(c.moduleKey));

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const active = CATEGORIES.find(c => c.key === activeKey) || CATEGORIES[0];

  return (
    <div ref={wrapRef} className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {/* Trigger */}
      <button
        type="button"
        aria-expanded={open}
        className={cn(
          "inline-flex items-center gap-1.5 px-4 py-2 rounded-pill font-medium text-sm transition-all",
          open ? "text-primary bg-primary/5" : "text-foreground/80 hover:text-primary"
        )}
      >
        <Sparkles className="w-4 h-4 text-healing-green" />
        Explore
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown className="w-3.5 h-3.5" />
        </motion.span>
      </button>

      {/* Invisible hover bridge so the panel doesn't close when cursor moves down */}
      {open && <div className="absolute left-0 right-0 top-full h-3" />}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-x-4 top-[112px] z-[60] mx-auto max-w-[1120px] 2xl:max-w-[1180px]"
          >
            {/* Subtle outer glow */}
            <div
              aria-hidden
              className="absolute -inset-px rounded-[24px] opacity-50 blur-[6px] pointer-events-none"
              style={{ background: "linear-gradient(135deg, hsl(213 69% 39% / 0.35), hsl(152 62% 44% / 0.35))" }}
            />

            <div
              className="relative rounded-[22px] overflow-hidden border border-border/60 bg-card shadow-[0_24px_60px_-20px_rgba(15,42,90,0.35)]"
            >
              {/* Layout: 3 distinct cards */}
              <div className="grid grid-cols-12 gap-2 2xl:gap-3 p-2 2xl:p-3 bg-gradient-to-br from-muted/40 via-background to-healing-green/5 h-[min(560px,calc(100vh-140px))] overflow-hidden">

                {/* CARD 1 — Categories */}
                <section className="col-span-4 rounded-2xl bg-card border border-border/60 shadow-sm flex flex-col min-h-0">
                  <header className="px-4 pt-4 pb-2 flex items-center gap-2">
                    <span className="text-[10px] font-bold tracking-[0.18em] text-muted-foreground uppercase">
                      Healthcare Ecosystem
                    </span>
                    <span className="h-px flex-1 bg-border" />
                    <span className="text-[10px] font-semibold text-primary">{visibleCategories.length}</span>
                  </header>
                  <div className="px-2 pb-3 space-y-0.5 overflow-y-auto overflow-x-hidden custom-scroll flex-1 min-h-0">
                    {visibleCategories.map((cat) => {
                      const Icon = cat.icon;
                      const isActive = activeKey === cat.key;
                      return (
                        <Link
                          key={cat.key}
                          to={cat.href}
                          onMouseEnter={() => setActiveKey(cat.key)}
                          onFocus={() => setActiveKey(cat.key)}
                          onClick={() => setOpen(false)}
                          className={cn(
                            "group flex items-center gap-3 p-2.5 rounded-xl border transition-all",
                            isActive
                              ? "bg-primary/[0.06] border-primary/20"
                              : "border-transparent hover:bg-muted/60"
                          )}
                        >
                          <div
                            className={cn(
                              "w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all",
                              isActive ? "shadow-md" : ""
                            )}
                            style={{
                              background: isActive
                                ? "linear-gradient(135deg, hsl(213 69% 39%), hsl(152 62% 44%))"
                                : "linear-gradient(135deg, hsl(213 69% 39% / 0.10), hsl(152 62% 44% / 0.10))",
                            }}
                          >
                            <Icon className={cn("w-5 h-5", isActive ? "text-white" : "text-primary")} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="font-semibold text-sm text-foreground truncate">{cat.label}</p>
                              {cat.badge && <BadgePill kind={cat.badge} />}
                            </div>
                            <p className="text-[11px] text-muted-foreground truncate">{cat.desc}</p>
                          </div>
                          <ChevronRight className={cn(
                            "w-4 h-4 shrink-0 transition-all",
                            isActive ? "text-primary translate-x-0.5" : "text-muted-foreground/50"
                          )} />
                        </Link>
                      );
                    })}
                  </div>
                </section>

                {/* CARD 2 — Popular subcategories for active */}
                <section className="col-span-5 rounded-2xl bg-card border border-border/60 shadow-sm flex flex-col min-h-0 overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={active.key}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.18 }}
                      className="flex flex-col h-full"
                    >
                      <header className="px-5 pt-4 pb-3 flex items-center justify-between border-b border-border/50">
                        <div className="min-w-0">
                          <p className="text-[10px] font-bold tracking-[0.18em] text-muted-foreground uppercase">
                            Popular in
                          </p>
                          <h3 className="text-base font-bold text-foreground truncate">{active.label}</h3>
                        </div>
                        <Link
                          to={active.href}
                          onClick={() => setOpen(false)}
                          className="text-xs font-semibold text-primary hover:text-primary/80 inline-flex items-center gap-1 shrink-0"
                        >
                          View all <ArrowRight className="w-3 h-3" />
                        </Link>
                      </header>

                       <div className="p-3 2xl:p-4 grid grid-cols-2 gap-2 overflow-y-auto overflow-x-hidden custom-scroll">
                        {active.subs.map((sub) => (
                          <Link
                            key={sub.label}
                            to={sub.href}
                            onClick={() => setOpen(false)}
                            className="group flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border border-border/60 bg-background hover:bg-primary/[0.04] hover:border-primary/30 transition-all"
                          >
                            <span className="text-sm font-medium text-foreground/90 group-hover:text-primary truncate">
                              {sub.label}
                            </span>
                            {sub.hot ? (
                              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-cta-orange shrink-0">
                                <TrendingUp className="w-2.5 h-2.5" /> HOT
                              </span>
                            ) : (
                              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/60 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                            )}
                          </Link>
                        ))}
                      </div>

                      {/* Trending row */}
                      <div className="mt-auto px-4 2xl:px-5 py-3 2xl:py-4 border-t border-border/50 bg-muted/30 rounded-b-2xl">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-3.5 h-3.5 text-cta-orange" />
                          <span className="text-[10px] font-bold tracking-[0.15em] text-muted-foreground uppercase">
                            Trending Searches
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {TRENDING.map((t) => (
                            <Link
                              key={t}
                              to={`/listings?search=${encodeURIComponent(t)}`}
                              onClick={() => setOpen(false)}
                              className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-background border border-border/60 text-foreground/80 hover:border-primary/30 hover:text-primary transition-colors"
                            >
                              {t}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </section>

                {/* CARD 3 — AI + Quick actions */}
                <section className="col-span-3 flex flex-col gap-2 2xl:gap-3 min-h-0 overflow-hidden">
                  {/* AI tools */}
                  <div
                    className="rounded-2xl border border-primary/20 shadow-sm overflow-hidden relative"
                    style={{ background: "linear-gradient(160deg, hsl(213 69% 39% / 0.06), hsl(152 62% 44% / 0.08))" }}
                  >
                    <div className="p-3 2xl:p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center shadow-sm"
                          style={{ background: "linear-gradient(135deg, hsl(213 69% 39%), hsl(152 62% 44%))" }}>
                          <Sparkles className="w-3.5 h-3.5 text-white" />
                        </div>
                        <p className="text-xs font-bold text-foreground">AI Healthcare Tools</p>
                      </div>
                      <div className="space-y-1.5">
                        {AI_TOOLS.map((t) => (
                          <Link
                            key={t.label}
                            to={t.href}
                            onClick={() => setOpen(false)}
                            className="group flex items-center gap-2.5 p-2 rounded-xl bg-card/70 hover:bg-card border border-transparent hover:border-primary/20 transition-all"
                          >
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                              <t.icon className="w-4 h-4 text-primary" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[12px] font-semibold text-foreground truncate">{t.label}</p>
                              <p className="text-[10px] text-muted-foreground truncate">{t.desc}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex-1 rounded-2xl bg-card border border-border/60 shadow-sm p-3 2xl:p-4 flex flex-col min-h-0 overflow-y-auto overflow-x-hidden custom-scroll">
                    <p className="text-[10px] font-bold tracking-[0.18em] text-muted-foreground uppercase mb-3">
                      Quick Actions
                    </p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {QUICK_ACTIONS.map((q) => (
                        <Link
                          key={q.label}
                          to={q.href}
                          onClick={() => setOpen(false)}
                          className="group flex flex-col items-center justify-center text-center gap-1.5 p-2.5 rounded-xl border border-border/60 bg-background hover:border-healing-green/40 hover:bg-healing-green/[0.04] transition-all"
                        >
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                            style={{ background: "linear-gradient(135deg, hsl(213 69% 39% / 0.10), hsl(152 62% 44% / 0.15))" }}>
                            <q.icon className="w-4 h-4 text-primary" />
                          </div>
                          <span className="text-[10.5px] font-semibold text-foreground leading-tight">
                            {q.label}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PremiumMegaMenu;
